import { Injectable, inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { catchError, of, switchMap, tap } from 'rxjs';

import { FeaturesService, UploadService } from '@offconon/admin-api';
import { FeaturesService as CoreFeaturesService } from '@offconon/core-api';

import * as AdminAcademyActions from './admin-academy.actions';

@Injectable()
export class AdminAcademyEffects {
  private actions$ = inject(Actions);
  private featuresService = inject(FeaturesService);
  private coreFeaturesService = inject(CoreFeaturesService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private uploadService = inject(UploadService);

  fetchAcademyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.fetchAcademyList),
      switchMap((action) =>
        this.featuresService
          .featuresAcademyList(action.page, action.pageSize, action.searchJson)
          .pipe(
            switchMap((adminAcademy) => [
              AdminAcademyActions.fetchAcademyListSuccess({
                adminAcademy: adminAcademy,
              }),
            ]),
          ),
      ),
    ),
  );

  createAcademy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.createAcademy),
      switchMap((action) =>
        this.featuresService.featuresAcademyCreate(action.academy).pipe(
          switchMap((adminAcademy) => [
            AdminAcademyActions.createAcademySuccess({
              academy: adminAcademy,
            }),
            AdminAcademyActions.createAcademySuccess({
              academy: adminAcademy,
            }),
          ]),
          catchError((error) => of(AdminAcademyActions.loadAdminAcademyFailure({ error }))),
        ),
      ),
    ),
  );

  updateAcademy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.updateAcademy),
      switchMap((action) =>
        this.featuresService.featuresAcademyCreate(action.academy).pipe(
          switchMap((adminAcademy) => [
            AdminAcademyActions.updateAcademySuccess({
              academy: adminAcademy,
            }),
          ]),
        ),
      ),
    ),
  );

  deleteAcademy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.deleteAcademy),
      switchMap((action) =>
        this.featuresService.featuresAcademyDestroy(action.id).pipe(
          switchMap(() => [
            AdminAcademyActions.deleteAcademySuccess({
              id: action.id,
            }),
          ]),
        ),
      ),
    ),
  );

  fetchAcademyById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.fetchAcademyById),
      switchMap((action) =>
        this.coreFeaturesService
          .featuresAcademyDetailsRetrieve({ id: action.id, parent_id: action.parentId })
          .pipe(
            switchMap((academy) => [
              AdminAcademyActions.fetchAcademyByIdSuccess({
                academy,
              }),
            ]),
          ),
      ),
    ),
  );

  createSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.createSection),
      switchMap((action) =>
        this.featuresService.featuresAcademyCreate(action.academy).pipe(
          switchMap((academy) => [
            AdminAcademyActions.createSectionSuccess({
              academy,
            }),
          ]),
        ),
      ),
    ),
  );

  updateSectionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.updateSectionStatus),
      switchMap((action) =>
        this.featuresService
          .featuresAcademyCreate({
            academy: {
              id: action?.id,
              active: action.active,
            },
          })
          .pipe(
            tap(() => this.showMessage('Section status updated successfully.')),

            switchMap((adminAcademy) => [
              AdminAcademyActions.updateSectionStatusSuccess({
                academy: adminAcademy,
              }),
            ]),
          ),
      ),
    ),
  );

  deleteSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.deleteSection),
      switchMap((action) =>
        this.featuresService.featuresAcademyDestroy(action.id).pipe(
          tap(() => this.showMessage('Section deleted successfully.')),
          switchMap(() => [
            AdminAcademyActions.deleteSectionSuccess({
              id: action.id,
            }),
          ]),
        ),
      ),
    ),
  );

  showMessage(message: string) {
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: this.translateService.instant(message),
    });
  }

  uploadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAcademyActions.uploadImage),
      switchMap((action) =>
        this.uploadService
          .uploadUploadImageCreate(
            'academy',
            action.upload_type,
            action.image_file,
            action.image_cropped_file,
            action.id,
          )
          .pipe(
            switchMap((adminAcademy) => [
              AdminAcademyActions.uploadImageSuccess({
                cropped_image_url: adminAcademy.cropped_image_url,
                image_url: adminAcademy.image_url,
                upload_type: action.upload_type,
              }),
            ]),
          ),
      ),
    ),
  );
}
